var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { isFuture } from "date-fns";
import { ConsentStatus, ConsentType, ConsentUpdate, ConsentUpdateBatch, ConsentUpdateFromUI, getPrimaryConsentTypeForProject, IdentifiedPatientLastActivityType, isConsentDataSensitivityScopeValidForConsent, primaryConsentTypes, } from "healthex-common/consent.dtos";
import { StudyParticipationType, StudyPatientConsentStatusSearch, StudyStatus, } from "healthex-common/study.dtos";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { filterNull, mapValues, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { ParameterizedPagedRemoteData, RemoteData } from "../Infra/RemoteData";
const PAGE_SIZE = 100;
export class StudyManager {
    constructor(provisioningService, authManager, statsService, studyService, patientService, invalidationManager, studyRecruitmentManager, analyticsService) {
        var _a, _b;
        this.provisioningService = provisioningService;
        this.authManager = authManager;
        this.statsService = statsService;
        this.studyService = studyService;
        this.patientService = patientService;
        this.invalidationManager = invalidationManager;
        this.studyRecruitmentManager = studyRecruitmentManager;
        this.analyticsService = analyticsService;
        this.allPatientConsentsByStudyForSearchTotals = new RemoteData(this.invalidationManager, () => this.loadTotalsForStudiesByConsent());
        this.allPatientConsentsByStudyForSearch = new ParameterizedPagedRemoteData(this.invalidationManager, PAGE_SIZE, () => { }, (input) => this.loadAllStudiesForSearch(input));
        this.pendingPatientConsentsByStudyForSearch = new ParameterizedPagedRemoteData(this.invalidationManager, PAGE_SIZE, () => this.patientStudiesSearchInput, (input) => this.loadPendingStudiesForSearch(input));
        this.optedInPatientConsentsByStudyForSearch = new ParameterizedPagedRemoteData(this.invalidationManager, PAGE_SIZE, () => this.patientStudiesSearchInput, (input) => this.loadOptedInStudiesForSearch(input));
        this.optedOutPatientConsentsByStudyForSearch = new ParameterizedPagedRemoteData(this.invalidationManager, PAGE_SIZE, () => this.patientStudiesSearchInput, (input) => this.loadOptedOutStudiesForSearch(input));
        this.availableDiseaseAreas = new RemoteData(this.invalidationManager, () => this.studyService.getExistingDiseaseAreas());
        this.submittingConsent = false;
        this.allPatientConsentsByStudyForSearchAdditionalSearchInput = {
            consentType: primaryConsentTypes,
            includeConsentingByLink: true,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            distinctPending: true,
            includePatientStudyConsentStatuses: [ConsentStatus.OPTED_IN, ConsentStatus.OPTED_OUT],
            study: {
                excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                excludeDeId: true,
                patientConsentStatusSearch: StudyPatientConsentStatusSearch.INCLUDE_ALL_OPTED_IN,
                patientId: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
            },
        };
    }
    get patientStudiesSearchInput() {
        const userId = this.authManager.id;
        return {
            addedByOrganizationdId: this.authManager.orgId,
            participatingPatientId: userId !== null && userId !== void 0 ? userId : undefined,
        };
    }
    async internalLoadStudiesByAllConsentsForSearch(input, additionalSearchInput) {
        const orgId = this.authManager.orgId;
        const userId = this.authManager.id;
        if (!orgId || !userId) {
            return { results: [], total: 0 };
        }
        const { start, end, ...searchInput } = input;
        const studySearchResult = await this.patientService.postSearchForPatientStudyConsents(orgId, userId, {
            ...searchInput,
            ...additionalSearchInput,
        }, start, end);
        const results = filterNull(studySearchResult.results.map((result) => {
            return {
                ...result.study,
                consents: result.consents,
            };
        }));
        return {
            results,
            total: studySearchResult.total,
        };
    }
    async internalLoadStudiesByAllConsentsForSearchTotals(input, additionalSearchInput) {
        const orgId = this.authManager.orgId;
        const userId = this.authManager.id;
        if (!orgId || !userId) {
            return { all: 0, pending: 0, optedIn: 0, optedOut: 0 };
        }
        const { start, end, ...searchInput } = input;
        return await this.patientService.postSearchForPatientStudyConsentsTotals(orgId, userId, {
            ...searchInput,
            ...additionalSearchInput,
        }, start, end);
    }
    async internalLoadTotalsForStudiesByConsent(input, additionalSearchInput) {
        return this.internalLoadStudiesByAllConsentsForSearchTotals(input, additionalSearchInput);
    }
    async loadTotalsForStudiesByConsent() {
        return await this.internalLoadTotalsForStudiesByConsent({ start: 0, end: undefined }, {
            ...this.allPatientConsentsByStudyForSearchAdditionalSearchInput,
        });
    }
    async loadAllStudiesForSearch(input) {
        return this.internalLoadStudiesByAllConsentsForSearch(input, {
            ...this.allPatientConsentsByStudyForSearchAdditionalSearchInput,
        });
    }
    async loadPendingStudiesForSearch(input) {
        var _a;
        return this.internalLoadStudiesByAllConsentsForSearch(input, {
            consentType: primaryConsentTypes,
            pending: true,
            includeConsentingByLink: true,
            distinctPending: true,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            study: {
                excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                excludeDeId: true,
            },
        });
    }
    async loadOptedInStudiesForSearch(input) {
        var _a, _b;
        return this.internalLoadStudiesByAllConsentsForSearch(input, {
            consentType: primaryConsentTypes,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            pending: false,
            distinctPending: true,
            includePatientStudyConsentStatuses: [ConsentStatus.OPTED_IN],
            study: {
                excludeDeId: true,
                patientId: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                patientConsentStatusSearch: StudyPatientConsentStatusSearch.OPTED_IN,
            },
        });
    }
    async loadOptedOutStudiesForSearch(input) {
        var _a, _b;
        return this.internalLoadStudiesByAllConsentsForSearch(input, {
            consentType: primaryConsentTypes,
            patientState: (_a = this.authManager.state) !== null && _a !== void 0 ? _a : undefined,
            pending: false,
            distinctPending: true,
            includePatientStudyConsentStatuses: [ConsentStatus.OPTED_OUT],
            study: {
                excludeStatuses: [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN],
                excludeDeId: true,
                patientId: (_b = this.authManager.id) !== null && _b !== void 0 ? _b : undefined,
                patientConsentStatusSearch: StudyPatientConsentStatusSearch.OPTED_OUT,
            },
        });
    }
    async expandStudy(study) {
        let users;
        try {
            if (!this.authManager.isResearcher) {
                users = [];
            }
            else {
                users = await this.studyService.getStudyUsers(study.addedByOrganization.id, study.id);
            }
        }
        catch (e) {
            users = [];
        }
        return {
            ...study,
            stats: await this.statsService.getStatsCurrentForStudy(study.addedByOrganization.id, study.id),
            users,
        };
    }
    async reloadAllStudies() {
        await this.allPatientConsentsByStudyForSearch.waitForReload();
        await this.allPatientConsentsByStudyForSearchTotals.waitForReload();
        await this.pendingPatientConsentsByStudyForSearch.waitForReload();
        await this.optedInPatientConsentsByStudyForSearch.waitForReload();
        await this.optedOutPatientConsentsByStudyForSearch.waitForReload();
    }
    async getStudy(studyId, versionType) {
        if (!this.authManager.orgId)
            return null;
        const study = await this.studyService.getStudyById(studyId, this.authManager.orgId, versionType);
        return study ? await this.expandStudy(study) : study;
    }
    async getStudyPublicInfo(studyId, versionType) {
        return await this.studyService.getStudyPublicInfoById(studyId, versionType);
    }
    async getActiveStudyConsents(studyId) {
        var _a;
        if (this.authManager.orgId && this.authManager.id && this.authManager.userType === "PATIENT") {
            const studyConsents = await this.patientService.getPatientStudyConsent(this.authManager.orgId, this.authManager.id, studyId);
            if (!studyConsents)
                return [];
            return (_a = studyConsents.consents.filter((consent) => isFuture(consent.expiration)).map((consent) => consent.consentType)) !== null && _a !== void 0 ? _a : [];
        }
        else {
            return null;
        }
    }
    get canCreateStudy() {
        return this.authManager.isLoggedIn && !!this.authManager.orgId && this.authManager.isResearcher;
    }
    async createStudy(input) {
        const canCreate = this.canCreateStudy;
        if (!canCreate || !this.authManager.orgId) {
            return null;
        }
        const studyId = await this.studyService.createStudy(this.authManager.orgId, input);
        if (studyId && input.hipaaAuthorizationForm) {
            const fileUploadInfo = await this.studyService.getHipaaAuthorizationFormUploadUrl(this.authManager.orgId, studyId, input.hipaaAuthorizationForm);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.hipaaAuthorizationForm);
            await this.studyService.updateStudyVersion(studyId, this.authManager.orgId, {
                hipaaAuthorizationFormUrl: fileUploadInfo.fileUrl,
            });
        }
        await this.reloadAllStudies();
        return studyId;
    }
    async updateStudyVersion(studyId, input) {
        const canCreate = this.canCreateStudy;
        if (!canCreate || !this.authManager.orgId) {
            return;
        }
        await this.studyService.updateStudyVersion(studyId, this.authManager.orgId, input);
        if (input.hipaaAuthorizationForm) {
            const fileUploadInfo = await this.studyService.getHipaaAuthorizationFormUploadUrl(this.authManager.orgId, studyId, input.hipaaAuthorizationForm);
            await this.provisioningService.uploadFileToUrl(fileUploadInfo.signedUrl, input.hipaaAuthorizationForm);
            await this.studyService.updateStudyVersion(studyId, this.authManager.orgId, {
                hipaaAuthorizationFormUrl: fileUploadInfo.fileUrl,
            });
        }
        await this.reloadAllStudies();
    }
    async getStudyToImport(nctId) {
        const orgId = this.authManager.orgId;
        if (!orgId) {
            return null;
        }
        return await this.studyService.getClinicalStudyForImport(nctId, orgId);
    }
    async optInToStudy(consentingPatientInfo, studyDto, consentTypes, input) {
        input.consentDataResourceScopes = studyDto.consentDataResourceScopes;
        input.consentDataSensitivityScopes = studyDto.consentDataSensitivityScopes.filter((sensitivityScope) => consentTypes.some((consentType) => isConsentDataSensitivityScopeValidForConsent(consentType, sensitivityScope)));
        await this.updateStudyConsent(consentingPatientInfo, studyDto, consentTypes, ConsentStatus.OPTED_IN, input);
    }
    async optOutOfStudy(consentingPatientInfo, studyDto, consentTypes, input) {
        await this.updateStudyConsent(consentingPatientInfo, studyDto, consentTypes, ConsentStatus.OPTED_OUT, input);
    }
    async updateStudyConsent(consentingPatientInfo, studyDto, consentTypes, consentStatus, input) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11;
        const canUpdateStudyConsent = this.authManager.userType === "PATIENT" ||
            (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
                this.authManager.permissions.includes(UserPermissions.PROVIDER));
        if (!canUpdateStudyConsent)
            return;
        const orgId = this.authManager.orgId;
        if (!orgId)
            return;
        const update = new ConsentUpdateFromUI({
            consentUIFlow: (_a = input.consentUIFlow) !== null && _a !== void 0 ? _a : null,
            uiHash: (_b = input.uiHash) !== null && _b !== void 0 ? _b : null,
            consentDataResourceScopes: (_c = input.consentDataResourceScopes) !== null && _c !== void 0 ? _c : null,
            consentDataSensitivityScopes: (_d = input.consentDataSensitivityScopes) !== null && _d !== void 0 ? _d : null,
            expirationDate: (_e = input.expirationDate) !== null && _e !== void 0 ? _e : undefined,
            patientName: (_f = input.patientName) !== null && _f !== void 0 ? _f : null,
            signerName: (_g = input.signerName) !== null && _g !== void 0 ? _g : null,
            signingReason: (_h = input.signingReason) !== null && _h !== void 0 ? _h : null,
            revocationReason: (_j = input.revocationReason) !== null && _j !== void 0 ? _j : null,
            pagesViewed: (_k = input.pagesViewed) !== null && _k !== void 0 ? _k : null,
            mp4Url: (_l = input.mp4Url) !== null && _l !== void 0 ? _l : null,
            posterUrl: (_m = input.posterUrl) !== null && _m !== void 0 ? _m : null,
            consentedByLink: (_o = input.consentedByLink) !== null && _o !== void 0 ? _o : null,
            providerId: (_p = input.providerId) !== null && _p !== void 0 ? _p : null,
            providerSignerName: (_q = input.providerSignerName) !== null && _q !== void 0 ? _q : null,
            providerSigningReason: (_r = input.providerSigningReason) !== null && _r !== void 0 ? _r : null,
            parentGuardianSignerName: (_s = input.parentGuardianSignerName) !== null && _s !== void 0 ? _s : null,
            parentGuardianSigningReason: (_t = input.parentGuardianSigningReason) !== null && _t !== void 0 ? _t : null,
            additionalParentGuardianSignerName: (_u = input.additionalParentGuardianSignerName) !== null && _u !== void 0 ? _u : null,
            additionalParentGuardianSigningReason: (_v = input.additionalParentGuardianSigningReason) !== null && _v !== void 0 ? _v : null,
            additionalParentGuardianExemptSignature: (_w = input.additionalParentGuardianExemptSignature) !== null && _w !== void 0 ? _w : null,
            minorParticipantSignerName: (_x = input.minorParticipantSignerName) !== null && _x !== void 0 ? _x : null,
            minorParticipantExemptFromAssent: (_y = input.minorParticipantExemptFromAssent) !== null && _y !== void 0 ? _y : null,
            patientMRN: (_z = input.patientMRN) !== null && _z !== void 0 ? _z : null,
            larSignerName: (_0 = input.larSignerName) !== null && _0 !== void 0 ? _0 : null,
            larSigningReason: (_1 = input.larSigningReason) !== null && _1 !== void 0 ? _1 : null,
            larParticipantSignerName: (_2 = input.larParticipantSignerName) !== null && _2 !== void 0 ? _2 : null,
            larParticipantExemptSignature: (_3 = input.larParticipantExemptSignature) !== null && _3 !== void 0 ? _3 : null,
            larParticipantSigningReason: (_4 = input.larParticipantSigningReason) !== null && _4 !== void 0 ? _4 : null,
            witnessSkipSignature: (_5 = input.witnessSkipSignature) !== null && _5 !== void 0 ? _5 : null,
            witnessSignerName: (_6 = input.witnessSignerName) !== null && _6 !== void 0 ? _6 : null,
            isProxyAccess: (_7 = input.isProxyAccess) !== null && _7 !== void 0 ? _7 : null,
            fullRecordWithoutRestrictedData: (_8 = studyDto.fullRecordWithoutRestrictedData) !== null && _8 !== void 0 ? _8 : null,
            documentRetrievalLocations: (_9 = input.documentRetrievalLocations) !== null && _9 !== void 0 ? _9 : [],
            loggedInDuringConsent: (_10 = input.loggedInDuringConsent) !== null && _10 !== void 0 ? _10 : null,
            retrieval: (_11 = input.retrieval) !== null && _11 !== void 0 ? _11 : null,
        });
        this.submittingConsent = true;
        try {
            await this.patientService.updateStudyConsentBatch(orgId, consentingPatientInfo.id, studyDto.id, studyDto.studyVersionId, new ConsentUpdateBatch({
                consentTypes,
                update: new ConsentUpdate({
                    patientState: consentingPatientInfo.state,
                    consentStatus,
                    ...update,
                }),
            }));
            await this.reloadAllStudies();
        }
        finally {
            this.submittingConsent = false;
        }
        let consentedByLinkIdentifiedPatientUpdate = {};
        const primaryConsentType = getPrimaryConsentTypeForProject(studyDto);
        if ((consentStatus === ConsentStatus.OPTED_IN || consentStatus === ConsentStatus.OPTED_OUT) && orgId) {
            if (consentTypes.includes(primaryConsentType)) {
                consentedByLinkIdentifiedPatientUpdate = this.getSelfRegistrationIdentifiedPatientUpdate(consentingPatientInfo);
            }
        }
        let optInOrOutIdentifiedPatientUpdate = {};
        if (consentTypes.includes(primaryConsentType) || consentTypes.includes(ConsentType.DATA_AUTHORIZATION)) {
            if (consentStatus === ConsentStatus.OPTED_IN) {
                optInOrOutIdentifiedPatientUpdate = {
                    lastActivityType: consentTypes.includes(primaryConsentType)
                        ? IdentifiedPatientLastActivityType.CONSENTED_TO_ENROLLMENT
                        : IdentifiedPatientLastActivityType.CONSENTED_TO_PRESCREENING,
                };
            }
            else {
                optInOrOutIdentifiedPatientUpdate = {
                    lastActivityType: consentTypes.includes(primaryConsentType)
                        ? IdentifiedPatientLastActivityType.REVOKED_ENROLLMENT
                        : IdentifiedPatientLastActivityType.REVOKED_PRESCREENING,
                };
            }
        }
        if (Object.keys(consentedByLinkIdentifiedPatientUpdate).length || Object.keys(optInOrOutIdentifiedPatientUpdate).length) {
            await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(studyDto.id, orgId, consentingPatientInfo.id, {
                ...consentedByLinkIdentifiedPatientUpdate,
                ...optInOrOutIdentifiedPatientUpdate,
            });
        }
    }
    async updateIdentifiedPatientImInterested(studyId, consentingPatientInfo) {
        const orgId = this.authManager.orgId;
        if (!orgId)
            return;
        await this.studyRecruitmentManager.updateIdentifiedPatientForStudy(studyId, orgId, consentingPatientInfo.id, {
            interested: true,
            ...this.getSelfRegistrationIdentifiedPatientUpdate(consentingPatientInfo),
        });
    }
    getSelfRegistrationIdentifiedPatientUpdate(consentingPatientInfo) {
        var _a, _b, _c, _d, _e;
        if (consentingPatientInfo.orgType === UserType.HEALTHCARE_ORGANIZATION) {
            return {
                mrn: (_a = consentingPatientInfo.mrn) !== null && _a !== void 0 ? _a : undefined,
            };
        }
        else if (consentingPatientInfo.orgType === UserType.EXTERNAL_RESEARCHER) {
            return {
                firstName: (_b = consentingPatientInfo.firstName) !== null && _b !== void 0 ? _b : undefined,
                lastName: (_c = consentingPatientInfo.lastName) !== null && _c !== void 0 ? _c : undefined,
                email: (_d = consentingPatientInfo.email) !== null && _d !== void 0 ? _d : undefined,
                phone: (_e = consentingPatientInfo.phone) !== null && _e !== void 0 ? _e : undefined,
            };
        }
        else {
            return willNeverHappen(consentingPatientInfo);
        }
    }
    async requestPatientStudyConsentForIdentification(study, patients) {
        const requestResult = await this.patientService.requestIdentifiedConsent(study.id, study.addedByOrganization.id, {
            patients,
        });
        return {
            numberOfPatients: requestResult.successPatients.length,
            successPatients: requestResult.successPatients,
            errorPatients: requestResult.errorPatients,
        };
    }
    async requestPatientStudyConsentForEnrollment(study, patients, isResend) {
        const requestResult = await this.patientService.requestEnrollmentConsent(study.id, study.addedByOrganization.id, {
            patients,
            isResend,
        });
        if (!requestResult)
            return null;
        return {
            numberOfPatients: requestResult.successPatients.length,
            successPatients: requestResult.successPatients,
            errorPatients: requestResult.errorPatients,
        };
    }
    async updateHealthRecords(study, patients) {
        await this.patientService.updateHealthRecords(study.id, study.addedByOrganization.id, {
            patients,
        });
    }
    async completeStudy(study) {
        await this.updateStudy(study, { status: StudyStatus.COMPLETED });
    }
    async withdrawStudy(study) {
        await this.updateStudy(study, { status: StudyStatus.WITHDRAWN });
    }
    async updateStudy(study, update) {
        await this.studyService.updateStudy(study.id, study.addedByOrganization.id, update);
        void this.reloadAllStudies();
    }
    async deleteStudyDraft(study) {
        await this.studyService.deleteStudyDraft(study.id, study.addedByOrganization.id);
        void this.reloadAllStudies();
    }
    async fetchOverviewStats(study, organizationId) {
        return await this.studyService.getOverviewStats(study.addedByOrganization.id, study.id, organizationId);
    }
    async fetchEnrollmentMetrics(study) {
        return await this.analyticsService.getStudyMetrics(study.addedByOrganization.id, study.id);
    }
    async fetchWhatsNew(study) {
        var _a;
        const userId = this.authManager.id;
        if (!userId) {
            return null;
        }
        const sinceResponse = await this.studyService.getWhatsNewLastSeen(study.addedByOrganization.id, study.id, userId);
        const sinceTimestamp = (_a = sinceResponse === null || sinceResponse === void 0 ? void 0 : sinceResponse.lastSeen) === null || _a === void 0 ? void 0 : _a.getTime().toString().padEnd(13, "0");
        const whatsNewInfo = await this.studyService.getWhatsNew(study.addedByOrganization.id, study.id, sinceTimestamp);
        void this.studyService.updateWhatsNewLastSeen(study.addedByOrganization.id, study.id, userId);
        return whatsNewInfo;
    }
    async fetchStudies(versionType, input, additionalSearchInput) {
        if (!this.authManager.orgId)
            return { results: [], total: 0 };
        const { start, end, ...studiesSearchInput } = input;
        const response = await this.studyService.postSearchForStudies(this.authManager.orgId, versionType, {
            ...studiesSearchInput,
            ...additionalSearchInput,
        }, start, end);
        return { results: response.results, total: response.total };
    }
    async fetchStudiesTotal(versionType, input) {
        const response = await this.studyService.postSearchForStudiesCount(versionType, Object.values(input));
        return mapValues(input, (_value, index) => response[index]);
    }
    checkStudyConsentType(consentType, consents) {
        return !!consents.find((consent) => consent.consentType === consentType);
    }
    getStudyParticipationType(study) {
        if (study.participationType) {
            return study.participationType;
        }
        return StudyParticipationType.ENROLLMENT;
    }
    async generatePatientFacingDetails(studyId, studyInput, field) {
        if (!this.authManager.orgId) {
            return null;
        }
        return await this.studyService.generatePatientFacingDetails(this.authManager.orgId, studyId, studyInput, field);
    }
    async translateStudyFields(studyId, input) {
        if (!this.authManager.orgId) {
            return null;
        }
        return await this.studyService.translateStudyFields(this.authManager.orgId, studyId, input);
    }
    async isAIGenerationRequired(studyId, studyInput) {
        if (!this.authManager.orgId) {
            return { required: true, currentHash: null };
        }
        return await this.studyService.isAIGenerationRequired(this.authManager.orgId, studyId, studyInput);
    }
    async createOrUpdateStudyUser(studyId, user, input) {
        if (!this.authManager.orgId)
            return;
        await this.studyService.createOrUpdateStudyUser(this.authManager.orgId, studyId, user.id, input);
    }
    async removeStudyUser(studyId, user) {
        if (!this.authManager.orgId)
            return;
        await this.studyService.deleteStudyUser(this.authManager.orgId, studyId, user.id);
    }
    async sendStudyInformation(study, patients) {
        await this.patientService.postSendPatientStudyInformation(study.id, study.addedByOrganization.id, {
            patients,
        });
    }
    async getStudyReminder(studyId) {
        if (!this.authManager.orgId)
            return null;
        return await this.studyService.getStudyReminder(this.authManager.orgId, studyId);
    }
    async upsertStudyReminder(studyId, input) {
        if (!this.authManager.orgId)
            return;
        await this.studyService.upsertStudyReminder(this.authManager.orgId, studyId, input);
    }
    async createStudyDraftIfNecessary(studyId) {
        if (!this.authManager.orgId)
            return;
        await this.studyService.createStudyDraftIfNecessary(studyId, this.authManager.orgId);
    }
    async getStudyDraftVersionNumber(studyId) {
        if (!this.authManager.orgId)
            return null;
        return await this.studyService.getStudyDraftVersionNumber(studyId, this.authManager.orgId);
    }
    async getStudyVersions(studyId) {
        if (!this.authManager.orgId)
            return [];
        return await this.studyService.getStudyVersions(studyId, this.authManager.orgId);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], StudyManager.prototype, "submittingConsent", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManager.prototype, "patientStudiesSearchInput", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManager.prototype, "canCreateStudy", null);
